'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

import Icon from "@/components/ui/AppIcon";
import AppImage from "@/components/ui/AppImage";
import { createClient } from '@/lib/supabase/client';
import { useAuth, UserRole } from '@/contexts/AuthContext';
import CustomerCardPanel from '@/components/common/CustomerCardPanel';
import { mapPipelineRow } from '@/components/common/CustomerCardPanel';
import type { CustomerCard } from '@/components/common/CustomerCardPanel';

interface Job {
  id: string;
  customer: string;
  address: string;
  status: 'in-progress' | 'on-hold' | 'completed' | 'scheduled' | 'delayed';
  progress: number;
  crew: string;
  crewSize: number;
  startDate: string;
  endDate: string;
  type: string;
  value: string;
  alerts: string[];
  assigned_pm_id?: string;
  photos?: { src: string; alt: string }[];
}

interface CrewOption {
  id: string;
  name: string;
  isAvailable: boolean;
  foremanName: string;
  contactPhone: string;
}

type HealthFlag = 'delayed' | 'missing-crew' | 'overdue-qc';

interface HealthIndicator {
  flag: HealthFlag;
  label: string;
  color: string;
  icon: string;
}

const statusConfig: Record<string, { label: string; bg: string; text: string; bar: string }> = {
  'in-progress': { label: "In Progress", bg: "bg-primary/10", text: "text-primary", bar: "bg-primary" },
  'on-hold': { label: "On Hold", bg: "bg-warning/10", text: "text-warning", bar: "bg-warning" },
  'completed': { label: "Completed", bg: "bg-success/10", text: "text-success", bar: "bg-success" },
  'scheduled': { label: "Scheduled", bg: "bg-secondary/10", text: "text-secondary", bar: "bg-secondary" },
  'delayed': { label: "Delayed", bg: "bg-error/10", text: "text-error", bar: "bg-error" },
};

type FilterStatus = 'all' | 'in-progress' | 'delayed' | 'on-hold' | 'scheduled' | 'completed';

const DEMO_JOBS: Job[] = [];

function mapRow(row: any): Job {
  const rawAlerts = Array.isArray(row.alerts) ? row.alerts : (row.alerts ? (() => { try { return JSON.parse(row.alerts); } catch { return []; } })() : []);
  return {
    id: row.id,
    customer: row.customer,
    address: row.address,
    status: row.job_status as Job['status'],
    progress: row.progress,
    crew: row.crew,
    crewSize: row.crew_size,
    startDate: row.start_date,
    endDate: row.end_date,
    type: row.job_type,
    value: row.value,
    alerts: rawAlerts.map((a: any) => typeof a === 'string' ? a : typeof a === 'object' && a !== null ? (a.message || a.type || JSON.stringify(a)) : String(a)),
    assigned_pm_id: row.assigned_pm_id,
  };
}

function getHealthIndicators(job: Job): HealthIndicator[] {
  const flags: HealthIndicator[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (job.status === 'delayed') {
    flags.push({ flag: 'delayed', label: 'Delayed', color: 'bg-error/15 text-error border-error/30', icon: 'ClockIcon' });
  }

  const noCrewAssigned = !job.crew || job.crew.trim() === '' || job.crew.toLowerCase() === 'unassigned' || job.crew.toLowerCase() === 'tbd';
  if (noCrewAssigned) {
    flags.push({ flag: 'missing-crew', label: 'No Crew', color: 'bg-warning/15 text-warning border-warning/30', icon: 'UserGroupIcon' });
  }

  if (job.endDate && job.status !== 'completed') {
    let endDate: Date | null = null;
    const slashParts = job.endDate.split('/');
    if (slashParts.length === 3) {
      endDate = new Date(parseInt(slashParts[2]), parseInt(slashParts[0]) - 1, parseInt(slashParts[1]));
    } else {
      endDate = new Date(job.endDate);
    }
    if (endDate && !isNaN(endDate.getTime())) {
      endDate.setHours(0, 0, 0, 0);
      if (endDate < today && job.progress < 100) {
        flags.push({ flag: 'overdue-qc', label: 'Overdue QC', color: 'bg-orange-100 text-orange-600 border-orange-200', icon: 'ExclamationTriangleIcon' });
      }
    }
  }

  return flags;
}

interface ReassignModalProps {
  job: Job;
  crews: CrewOption[];
  onClose: () => void;
  onReassign: (jobId: string, crewName: string) => void;
}

interface ActiveJobsGridProps {
  onJobClick?: () => void;
  onCustomerClick?: () => void;
}

function ReassignModal({ job, crews, onClose, onReassign }: ReassignModalProps) {
  const [selected, setSelected] = useState('');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-sm mx-4 p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-base font-semibold text-foreground">Reassign Crew</h3>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-muted transition-standard">
            <Icon name="XMarkIcon" size={16} className="text-muted-foreground" />
          </button>
        </div>
        <p className="font-caption text-xs text-muted-foreground mb-3">
          Job: <span className="font-medium text-foreground">{job.customer}</span>
        </p>
        <div className="space-y-2 max-h-52 overflow-y-auto mb-4">
          {crews.length === 0 ? (
            <p className="font-caption text-xs text-muted-foreground text-center py-4">No crews available</p>
          ) : (
            crews.map(c => (
              <button
                key={c.id}
                onClick={() => setSelected(c.name)}
                className={`w-full flex items-center justify-between p-3 rounded-lg border text-left transition-standard ${
                  selected === c.name ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40 hover:bg-muted'
                }`}
              >
                <div>
                  <p className="font-body text-sm font-medium text-foreground">{c.name}</p>
                  <p className="font-caption text-xs text-muted-foreground">{c.foremanName}</p>
                </div>
                <span className={`text-[10px] font-caption px-2 py-0.5 rounded-full ${
                  c.isAvailable ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                }`}>
                  {c.isAvailable ? 'Available' : 'Busy'}
                </span>
              </button>
            ))
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 px-3 py-2 bg-muted text-foreground rounded-lg font-body text-sm hover:bg-border transition-standard">
            Cancel
          </button>
          <button
            onClick={() => { if (selected) { onReassign(job.id, selected); onClose(); } }}
            disabled={!selected}
            className="flex-1 px-3 py-2 bg-primary text-white rounded-lg font-body text-sm font-medium hover:bg-secondary transition-standard disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Reassign
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ActiveJobsGrid({ onJobClick, onCustomerClick }: ActiveJobsGridProps) {
  const { currentUser, isSuperAdmin } = useAuth();
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [search, setSearch] = useState('');
  const [jobs, setJobs] = useState<Job[]>(DEMO_JOBS);
  const [orderedIds, setOrderedIds] = useState<string[]>(DEMO_JOBS.map(j => j.id));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [crews, setCrews] = useState<CrewOption[]>([]);
  const [showCrewOverlay, setShowCrewOverlay] = useState(false);
  const [reassignJob, setReassignJob] = useState<Job | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [allPhotos, setAllPhotos] = useState<{ src: string; alt: string }[]>([]);
  const [selectedPipelineCard, setSelectedPipelineCard] = useState<CustomerCard | null>(null);
  const [loadingPipelineCard, setLoadingPipelineCard] = useState<string | null>(null);

  const dragId = useRef<string | null>(null);
  const dragOverId = useRef<string | null>(null);

  const showSuccess = useCallback((msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 2500);
  }, []);

  useEffect(() => {
    const supabase = createClient();

    async function fetchData() {
      try {
        let query = supabase.from('jobs').select('*').order('created_at', { ascending: false });

        // Smart visibility: ICs see only their own jobs, all other roles see everything
        const broadAccessRoles: UserRole[] = ['Admin', 'Manager'];
        const userHasBroadAccess = currentUser?.roles?.some(r => broadAccessRoles.includes(r as UserRole)) ?? false;

        if (!userHasBroadAccess && currentUser?.id) {
          query = query.eq('assigned_pm_id', currentUser.id);
        }
        // Otherwise no filter — sees all jobs

        const { data: jobData, error: jobError } = await query;
        if (jobError) {
          setJobs(DEMO_JOBS);
          setOrderedIds(DEMO_JOBS.map(j => j.id));
        } else {
          const mapped = (jobData || []).map(mapRow);
          if (mapped.length > 0) {
            setJobs(mapped);
            setOrderedIds(mapped.map((j: Job) => j.id));
          } else {
            setJobs(DEMO_JOBS);
            setOrderedIds(DEMO_JOBS.map(j => j.id));
          }
        }

        const { data: crewData } = await supabase
          .from('installer_crews')
          .select('id, company_name, foreman_name, contact_phone, availability')
          .limit(20);

        if (crewData) {
          setCrews(crewData.map((c: any) => {
            const avail = c.availability || {};
            const isAvailable = typeof avail === 'object' && !Array.isArray(avail)
              ? Object.values(avail).some((v: any) => v === true || v === 'available')
              : true;
            return {
              id: c.id,
              name: c.company_name || 'Unknown Crew',
              isAvailable,
              foremanName: c.foreman_name || '',
              contactPhone: c.contact_phone || '',
            };
          }));
        }

        // Fetch photos to display on job cards
        const { data: photoData } = await supabase
          .from('photos')
          .select('src, alt, status')
          .not('src', 'is', null)
          .limit(60);

        if (photoData && photoData.length > 0) {
          setAllPhotos(photoData.map((p: any) => ({ src: p.src || '', alt: p.alt || 'Job photo' })));
        }
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchData();

    const channel = supabase
      .channel('jobs_realtime_grid')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'jobs' }, (payload) => {
        if (payload.eventType === 'INSERT') {
          const newJob = mapRow(payload.new);
          if (isSuperAdmin || newJob.assigned_pm_id === currentUser.id) {
            setJobs(prev => [newJob, ...prev]);
            setOrderedIds(prev => [newJob.id, ...prev]);
          }
        } else if (payload.eventType === 'UPDATE') {
          setJobs(prev => prev.map(j => j.id === payload.new.id ? mapRow(payload.new) : j));
        } else if (payload.eventType === 'DELETE') {
          const deletedId = (payload.old as any).id;
          setJobs(prev => prev.filter(j => j.id !== deletedId));
          setOrderedIds(prev => prev.filter(id => id !== deletedId));
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [currentUser.id, isSuperAdmin]);

  const orderedJobs = orderedIds
    .map(id => jobs.find(j => j.id === id))
    .filter((j): j is Job => !!j);

  const filtered = orderedJobs.filter(j => {
    const matchFilter = filter === 'all' || j.status === filter;
    const matchSearch = j.customer.toLowerCase().includes(search.toLowerCase()) ||
      j.id.toLowerCase().includes(search.toLowerCase()) ||
      j.address.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const availableCrews = crews.filter(c => c.isAvailable).length;
  const unassignedJobs = jobs.filter(j => !j.crew || j.crew.trim() === '' || j.crew.toLowerCase() === 'unassigned');

  function handleDragStart(id: string) { dragId.current = id; }
  function handleDragOver(e: React.DragEvent, id: string) {
    e.preventDefault();
    dragOverId.current = id;
  }
  function handleDrop() {
    if (!dragId.current || !dragOverId.current || dragId.current === dragOverId.current) return;
    setOrderedIds(prev => {
      const arr = [...prev];
      const fromIdx = arr.indexOf(dragId.current!);
      const toIdx = arr.indexOf(dragOverId.current!);
      if (fromIdx === -1 || toIdx === -1) return prev;
      arr.splice(fromIdx, 1);
      arr.splice(toIdx, 0, dragId.current!);
      return arr;
    });
    dragId.current = null;
    dragOverId.current = null;
  }

  async function handleMarkComplete(job: Job) {
    setActionLoading(job.id + '-complete');
    const supabase = createClient();
    const { error: updateError } = await supabase
      .from('jobs')
      .update({ job_status: 'completed', progress: 100 })
      .eq('id', job.id);
    if (!updateError) {
      setJobs(prev => prev.map(j => j.id === job.id ? { ...j, status: 'completed', progress: 100 } : j));
      showSuccess(`${job.customer} marked as complete`);
    }
    setActionLoading(null);
  }

  async function handleReassign(jobId: string, crewName: string) {
    const supabase = createClient();
    await supabase.from('jobs').update({ crew: crewName }).eq('id', jobId);
    setJobs(prev => prev.map(j => j.id === jobId ? { ...j, crew: crewName } : j));
    showSuccess(`Job reassigned to ${crewName}`);
  }

  const filters: { key: FilterStatus; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'in-progress', label: 'In Progress' },
    { key: 'delayed', label: 'Delayed' },
    { key: 'on-hold', label: 'On Hold' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'completed', label: 'Completed' },
  ];

  const openCustomerPanel = useCallback(async (job: Job) => {
    setLoadingPipelineCard(job.id);
    try {
      const supabase = createClient();
      // Try to find by customer name (first + last)
      const nameParts = job.customer.trim().split(' ');
      const firstName = nameParts[0] ?? '';
      const lastName = nameParts.slice(1).join(' ') || nameParts[0];

      let { data } = await supabase
        .from('customer_pipeline')
        .select('*')
        .ilike('customer_last_name', `%${lastName}%`)
        .limit(5);

      // If no match by last name, try first name
      if (!data || data.length === 0) {
        const res = await supabase
          .from('customer_pipeline')
          .select('*')
          .ilike('customer_first_name', `%${firstName}%`)
          .limit(5);
        data = res.data;
      }

      if (data && data.length > 0) {
        setSelectedPipelineCard(mapPipelineRow(data[0]));
      } else {
        // Fallback: open first pipeline record so panel is still useful
        const res = await supabase.from('customer_pipeline').select('*').limit(1);
        if (res.data && res.data.length > 0) {
          setSelectedPipelineCard(mapPipelineRow(res.data[0]));
        }
      }
    } catch {
      // silently fail
    } finally {
      setLoadingPipelineCard(null);
    }
  }, []);

  return (
    <>
      {reassignJob && (
        <ReassignModal
          job={reassignJob}
          crews={crews}
          onClose={() => setReassignJob(null)}
          onReassign={handleReassign}
        />
      )}

      {selectedPipelineCard && (
        <CustomerCardPanel
          card={selectedPipelineCard}
          onClose={() => setSelectedPipelineCard(null)}
        />
      )}

      <div className="bg-card rounded-xl shadow-sm border border-border">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Icon name="BriefcaseIcon" size={18} className="text-accent" variant="solid" />
              <h3 className="font-heading text-base font-semibold text-foreground">Active Jobs</h3>
              <span className="bg-accent/10 text-accent text-xs font-caption px-2 py-0.5 rounded-full">
                {loading ? '...' : `${jobs.length} total`}
              </span>
              {!loading && (
                <span className="flex items-center gap-1 text-[10px] font-caption text-success">
                  <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
                  Live
                </span>
              )}
              {!isSuperAdmin && (
                <span className="flex items-center gap-1 text-[10px] font-caption text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                  <Icon name="UserIcon" size={10} />
                  My Jobs
                </span>
              )}
              <span className="flex items-center gap-1 text-[10px] font-caption text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                <Icon name="ArrowsUpDownIcon" size={10} />
                Drag to reorder
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCrewOverlay(v => !v)}
                className={`flex items-center gap-1.5 px-4 rounded-lg font-caption text-xs border transition-standard ${
                  showCrewOverlay
                    ? 'bg-primary text-white border-primary' :'bg-muted text-muted-foreground border-border hover:border-primary/40 hover:text-foreground'
                }`}
                style={{ minHeight: '44px' }}
              >
                <Icon name="UserGroupIcon" size={13} />
                Crew Overlay
                {unassignedJobs.length > 0 && (
                  <span className={`w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center ${
                    showCrewOverlay ? 'bg-white text-primary' : 'bg-error text-white'
                  }`}>
                    {unassignedJobs.length}
                  </span>
                )}
              </button>
              <div className="relative">
                <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search jobs..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-8 pr-3 text-sm font-body bg-muted border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary/30 w-44"
                  style={{ minHeight: '44px' }}
                />
              </div>
            </div>
          </div>

          {/* Crew Availability Overlay Panel */}
          {showCrewOverlay && (
            <div className="mt-3 p-3 bg-muted/60 border border-border rounded-lg">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <p className="font-caption text-xs font-semibold text-foreground flex items-center gap-1.5">
                  <Icon name="UserGroupIcon" size={13} className="text-primary" />
                  Crew Availability Overview
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="flex items-center gap-1 text-[10px] font-caption text-success">
                    <span className="w-2 h-2 rounded-full bg-success" />
                    {availableCrews} available
                  </span>
                  <span className="flex items-center gap-1 text-[10px] font-caption text-error">
                    <span className="w-2 h-2 rounded-full bg-error" />
                    {crews.length - availableCrews} busy
                  </span>
                  {unassignedJobs.length > 0 && (
                    <span className="flex items-center gap-1 text-[10px] font-caption text-warning">
                      <Icon name="ExclamationTriangleIcon" size={10} />
                      {unassignedJobs.length} job{unassignedJobs.length > 1 ? 's' : ''} need crew
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {crews.slice(0, 8).map(c => (
                  <div key={c.id} className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-caption ${
                    c.isAvailable
                      ? 'bg-success/10 border-success/20 text-success' :'bg-error/10 border-error/20 text-error'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${c.isAvailable ? 'bg-success' : 'bg-error'}`} />
                    {c.name}
                  </div>
                ))}
                {crews.length === 0 && (
                  <p className="text-[10px] font-caption text-muted-foreground">No crew data available</p>
                )}
              </div>
            </div>
          )}

          {/* Filter tabs */}
          <div className="flex gap-1 mt-3 flex-wrap">
            {filters.map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 rounded-md font-caption text-xs transition-standard ${
                  filter === f.key
                    ? 'bg-accent text-white' : 'bg-muted text-muted-foreground hover:bg-border hover:text-foreground'
                }`}
                style={{ minHeight: '44px' }}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Success Toast */}
        {successMsg && (
          <div className="mx-5 mt-3 flex items-center gap-2 px-3 py-2 bg-success/10 border border-success/20 rounded-lg">
            <Icon name="CheckCircleIcon" size={14} className="text-success" variant="solid" />
            <p className="font-caption text-xs text-success">{successMsg}</p>
          </div>
        )}

        {/* Grid */}
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          {loading ? (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mb-3" />
              <p className="font-body text-sm text-muted-foreground">Loading jobs...</p>
            </div>
          ) : error ? (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
              <Icon name="ExclamationTriangleIcon" size={36} className="text-error mb-3" />
              <p className="font-body text-sm text-error">Failed to load jobs</p>
              <p className="font-caption text-xs text-muted-foreground mt-1">{error}</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
              <Icon name="MagnifyingGlassIcon" size={36} className="text-muted-foreground mb-3" />
              <p className="font-body text-sm text-muted-foreground">
                {jobs.length === 0 ? `No jobs assigned to ${currentUser.first_name}` : 'No jobs match your filters'}
              </p>
            </div>
          ) : (
            filtered.map((job) => {
              const s = statusConfig[job.status] ?? { label: job.status, bg: 'bg-muted', text: 'text-muted-foreground', bar: 'bg-muted-foreground' };
              const healthFlags = getHealthIndicators(job);
              const hasCrewSlot = !job.crew || job.crew.trim() === '' || job.crew.toLowerCase() === 'unassigned';
              const isCompleting = actionLoading === job.id + '-complete';
              const matchedCrew = crews.find(c => c.name === job.crew);

              return (
                <div
                  key={job.id}
                  draggable
                  onDragStart={() => handleDragStart(job.id)}
                  onDragOver={e => handleDragOver(e, job.id)}
                  onDrop={handleDrop}
                  onClick={() => openCustomerPanel(job)}
                  className="group bg-background border border-border rounded-xl p-4 hover:border-primary hover:shadow-md transition-standard cursor-pointer relative"
                >
                  {/* Drag handle hint */}
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-40 transition-standard pointer-events-none">
                    <Icon name="Bars3Icon" size={14} className="text-muted-foreground" />
                  </div>

                  {/* Crew availability overlay badge */}
                  {showCrewOverlay && hasCrewSlot && (
                    <div className="absolute -top-2 -left-2 z-10">
                      <span className="flex items-center gap-1 px-1.5 py-0.5 bg-warning text-white rounded-full text-[9px] font-caption font-bold shadow-sm">
                        <Icon name="UserGroupIcon" size={8} variant="solid" />
                        Needs Crew
                      </span>
                    </div>
                  )}

                  {/* Top row */}
                  <div className="flex items-start justify-between mb-2 pr-5">
                    <div>
                      <div className="flex items-center gap-2">
                        <button
                          className="font-heading text-base font-bold text-foreground hover:text-primary transition-standard text-left"
                          onClick={e => { e.stopPropagation(); openCustomerPanel(job); }}
                        >
                          {loadingPipelineCard === job.id ? (
                            <span className="flex items-center gap-1.5">
                              <span className="w-3 h-3 border border-primary border-t-transparent rounded-full animate-spin" />
                              {job.customer}
                            </span>
                          ) : job.customer}
                        </button>
                        {(job.alerts.length > 0 || healthFlags.length > 0) && (
                          <span className="w-2 h-2 rounded-full bg-error animate-pulse flex-shrink-0" />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <p className="font-data text-xs text-muted-foreground">{job.id}</p>
                        <button
                          onClick={e => { e.stopPropagation(); openCustomerPanel(job); }}
                          className="font-caption text-[10px] text-primary/70 hover:text-primary underline underline-offset-2 transition-standard"
                          title="View customer card"
                        >
                          View contact
                        </button>
                      </div>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-caption font-medium flex-shrink-0 ${s.bg} ${s.text}`}>
                      {s.label}
                    </span>
                  </div>

                  {/* Health Indicators */}
                  {healthFlags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {healthFlags.map(hf => (
                        <span key={hf.flag} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-caption font-medium ${hf.color}`}>
                          <Icon name={hf.icon as any} size={9} variant="solid" />
                          {hf.label}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Legacy alerts */}
                  {job.alerts.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {job.alerts.map((alert, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-error/10 text-error rounded border border-error/20 text-[10px] font-caption">
                          <Icon name="ExclamationTriangleIcon" size={9} variant="solid" />
                          {typeof alert === 'string' ? alert : typeof alert === 'object' && alert !== null ? ((alert as any).message || (alert as any).type || JSON.stringify(alert)) : String(alert)}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Address */}
                  <div className="flex items-start gap-1.5 mb-3">
                    <Icon name="MapPinIcon" size={12} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <p className="font-caption text-xs text-muted-foreground line-clamp-1">{job.address}</p>
                  </div>

                  {/* Progress */}
                  <div className="mb-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-caption text-xs text-muted-foreground">Progress</span>
                      <span className="font-data text-xs font-medium text-foreground">{job.progress}%</span>
                    </div>
                    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${s.bar}`}
                        style={{ width: `${job.progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Photo Thumbnail Strip */}
                  {(() => {
                    if (allPhotos.length === 0) return null;
                    const jobIndex = orderedIds.indexOf(job.id);
                    const startIdx = (jobIndex * 3) % allPhotos.length;
                    const cardPhotos = [
                      allPhotos[startIdx % allPhotos.length],
                      allPhotos[(startIdx + 1) % allPhotos.length],
                      allPhotos[(startIdx + 2) % allPhotos.length],
                    ];
                    const extraCount = allPhotos.length > 3 ? allPhotos.length - 3 : 0;
                    return (
                      <div className="mb-3">
                        <div className="flex items-center gap-1 mb-1.5">
                          <Icon name="PhotoIcon" size={11} className="text-muted-foreground" />
                          <span className="font-caption text-[10px] text-muted-foreground">Photos</span>
                          <span className="font-caption text-[10px] text-primary ml-auto">{allPhotos.length} total</span>
                        </div>
                        <div className="flex gap-1.5">
                          {cardPhotos.map((photo, i) => (
                            <div
                              key={i}
                              className="flex-1 aspect-square rounded-md overflow-hidden bg-muted border border-border"
                              onClick={e => { e.stopPropagation(); openCustomerPanel(job); }}
                            >
                              <AppImage
                                src={photo.src}
                                alt={photo.alt}
                                className="w-full h-full object-cover hover:scale-105 transition-standard"
                              />
                            </div>
                          ))}
                          {extraCount > 0 && (
                            <div
                              className="flex-1 aspect-square rounded-md bg-muted border border-border flex items-center justify-center cursor-pointer hover:bg-primary/10 transition-standard"
                              onClick={e => { e.stopPropagation(); openCustomerPanel(job); }}
                            >
                              <span className="font-caption text-[10px] font-semibold text-muted-foreground">+{extraCount}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Meta */}
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="flex items-center gap-1.5">
                      <Icon name="UserGroupIcon" size={12} className="text-muted-foreground" />
                      <span className={`font-caption text-xs truncate ${hasCrewSlot ? 'text-warning font-medium' : 'text-muted-foreground'}`}>
                        {hasCrewSlot ? 'Unassigned' : `${job.crew} (${job.crewSize})`}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Icon name="WrenchScrewdriverIcon" size={12} className="text-muted-foreground" />
                      <span className="font-caption text-xs text-muted-foreground truncate">{job.type}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Icon name="CalendarIcon" size={12} className="text-muted-foreground" />
                      <span className="font-caption text-xs text-muted-foreground">{job.startDate} – {job.endDate}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Icon name="CurrencyDollarIcon" size={12} className="text-muted-foreground" />
                      <span className="font-caption text-xs font-medium text-foreground">{job.value}</span>
                    </div>
                  </div>

                  {/* One-click Actions */}
                  <div className="flex gap-1.5 pt-2 border-t border-border">
                    {/* Call Crew */}
                    {!hasCrewSlot && matchedCrew?.contactPhone ? (
                      <a
                        href={`tel:${matchedCrew.contactPhone}`}
                        onClick={e => e.stopPropagation()}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-muted hover:bg-primary/10 hover:text-primary border border-border hover:border-primary/30 rounded-md font-caption text-xs text-muted-foreground transition-standard"
                        title="Call Crew"
                      >
                        <Icon name="PhoneIcon" size={11} variant="solid" />
                        Call
                      </a>
                    ) : (
                      <button
                        disabled
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-muted border border-border rounded-md font-caption text-xs text-muted-foreground/40 cursor-not-allowed"
                        title={hasCrewSlot ? 'No crew assigned' : 'No phone on file'}
                      >
                        <Icon name="PhoneIcon" size={11} variant="solid" />
                        Call
                      </button>
                    )}

                    {/* Reassign */}
                    <button
                      onClick={e => { e.stopPropagation(); setReassignJob(job); }}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-muted hover:bg-secondary/10 hover:text-secondary border border-border hover:border-secondary/30 rounded-md font-caption text-xs text-muted-foreground transition-standard"
                      title="Reassign Crew"
                    >
                      <Icon name="ArrowPathIcon" size={11} variant="solid" />
                      Reassign
                    </button>

                    {/* Mark Complete */}
                    {job.status !== 'completed' ? (
                      <button
                        onClick={e => { e.stopPropagation(); handleMarkComplete(job); }}
                        disabled={isCompleting}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-muted hover:bg-success/10 hover:text-success border border-border hover:border-success/30 rounded-md font-caption text-xs text-muted-foreground transition-standard disabled:opacity-50"
                        title="Mark Complete"
                      >
                        {isCompleting ? (
                          <span className="w-3 h-3 border border-success border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Icon name="CheckCircleIcon" size={11} variant="solid" />
                        )}
                        Done
                      </button>
                    ) : (
                      <div className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-success/10 border border-success/20 rounded-md font-caption text-xs text-success">
                        <Icon name="CheckCircleIcon" size={11} variant="solid" />
                        Complete
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}